import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";
import {
  Utensils,
  MapPin,
  Truck,
  Award,
  ArrowRight,
  Calendar,
  Star,
  Search,
  Smartphone,
  Quote,
  Flame,
  Mail,
  CalendarDays,
  Send,
  Loader2,
  Facebook,
  Building2,
} from "lucide-react";
import { api, fileUrl } from "@/api";
import { SEED_EVENT, isSeedEventVisible } from "@/data/seedEvent";
import MapView from "@/components/MapView";

const CUISINES = [
  "Tacos",
  "BBQ",
  "Burgers",
  "Desserts",
  "Coffee",
  "Pizza",
  "Healthy",
  "Italian",
  "Latin",
  "Mediterranean",
  "Cajun",
  "Vietnamese",
];

const TESTIMONIALS = [
  { quote: "Found my new favorite taco truck within minutes. The map is gorgeous!", name: "Mia R." },
  { quote: "Pre-ordering BBQ before lunch break is a game-changer.", name: "Devon K." },
  { quote: "Love the live status — no more wasted trips to closed trucks.", name: "Aisha P." },
];

function formatEventDate(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function VendorCard({ vendor }) {
  const tags = (vendor.cuisine_tags || []).slice(0, 2);
  return (
    <Link
      to={`/map?vendor=${vendor.vendor_id}`}
      data-testid={`featured-vendor-${vendor.vendor_id}`}
      className="group block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#E8650A]/40 hover:shadow-md transition"
    >
      <div className="aspect-[4/3] bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center relative overflow-hidden">
        {vendor.logo_url ? (
          <img
            src={fileUrl(vendor.logo_url)}
            alt={vendor.business_name}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <Utensils className="h-12 w-12 text-[#E8650A]/60" />
        )}
        {vendor.is_live && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-2 py-1 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
          </span>
        )}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-semibold bg-white/95 text-slate-900 px-2 py-1 rounded-full shadow-sm">
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> 5.0
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold text-slate-900 truncate">{vendor.business_name}</h3>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
          <MapPin className="h-3 w-3" />
          <span className="truncate">{vendor.address || "Location updating"}</span>
        </div>
        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="text-[10px] font-semibold uppercase tracking-wide bg-[#FDECE2] text-[#E8650A] px-2 py-0.5 rounded-full">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

function EventCard({ event }) {
  return (
    <div
      data-testid={`upcoming-event-${event.id}`}
      className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#E8650A]/40 hover:shadow-md transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#E8650A] bg-[#FDECE2] px-2.5 py-1 rounded-full">
          <Calendar className="h-3.5 w-3.5" />
          {event.date_label || formatEventDate(event.event_date)}
        </div>
        {event.vendor_logo_url ? (
          <img src={fileUrl(event.vendor_logo_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-slate-200" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-[#FDECE2] grid place-items-center">
            <Truck className="h-4 w-4 text-[#E8650A]" />
          </div>
        )}
      </div>
      <h3 className="mt-3 font-bold text-slate-900 leading-snug line-clamp-2">{event.title || event.name || "Food Truck Event"}</h3>
      {event.time_range_label && (
        <p className="mt-1.5 text-sm font-medium text-slate-700">{event.time_range_label}</p>
      )}
      {event.location && (
        <p className="mt-1.5 text-sm text-slate-500 line-clamp-2 flex items-start gap-1.5">
          <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{event.location}</span>
        </p>
      )}
      {event.vendor_name && (
        <p className="mt-3 text-xs text-slate-400">Hosted by <span className="font-medium text-slate-600">{event.vendor_name}</span></p>
      )}
    </div>
  );
}

function DemoModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", truck_name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        type: "demo",
        name: form.name,
        email: form.email,
        truck_name: form.truck_name || null,
        message: form.message,
      });
      toast.success("Demo request sent! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", truck_name: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      console.error("Demo submission failed:", err);
      toast.error("Couldn't send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="book-demo-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            Book a <span className="text-[#E8650A]">Demo</span>
          </DialogTitle>
          <DialogDescription>
            {"Tell us about your truck — we'll set up a personalized walkthrough."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="demo-name">Name</Label>
            <Input
              id="demo-name"
              data-testid="demo-name-input"
              value={form.name}
              onChange={update("name")}
              placeholder="Jane Cooper"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-email">Email</Label>
            <Input
              id="demo-email"
              data-testid="demo-email-input"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@email.com"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-truck">Truck Name</Label>
            <Input
              id="demo-truck"
              data-testid="demo-truck-input"
              value={form.truck_name}
              onChange={update("truck_name")}
              placeholder="Taco Titan"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="demo-message">Message</Label>
            <Textarea
              id="demo-message"
              data-testid="demo-message-input"
              value={form.message}
              onChange={update("message")}
              placeholder="What would you like to see?"
              rows={4}
              required
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="demo-close-btn"
            >
              Close
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="ff-btn-primary"
              data-testid="demo-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactSection({ onOpenDemo }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill name, email, subject and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", {
        type: "contact",
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      console.error("Contact submission failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-slate-200 bg-slate-50/60 scroll-mt-20"
    >
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: heading + info rows + book demo */}
          <div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Still have <span className="text-[#E8650A]">questions?</span>
            </h2>
            <p className="mt-4 text-slate-500 text-base max-w-md">
              {"We're here to help. Reach out and we'll get back to you within 24 hours."}
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="mailto:support@foodie-finder.com"
                data-testid="contact-email-row"
                className="flex items-center gap-4 group"
              >
                <div className="h-12 w-12 rounded-xl bg-[#FDECE2] grid place-items-center flex-shrink-0 group-hover:bg-[#E8650A] transition-colors">
                  <Mail className="h-5 w-5 text-[#E8650A] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Email Us</div>
                  <div className="text-sm text-slate-500">support@foodie-finder.com</div>
                </div>
              </a>

              <button
                type="button"
                onClick={onOpenDemo}
                data-testid="contact-book-demo-row"
                className="flex items-center gap-4 group text-left w-full"
              >
                <div className="h-12 w-12 rounded-xl bg-[#FDECE2] grid place-items-center flex-shrink-0 group-hover:bg-[#E8650A] transition-colors">
                  <CalendarDays className="h-5 w-5 text-[#E8650A] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Book a Demo</div>
                  <div className="text-sm text-slate-500">Get a personalized walkthrough</div>
                </div>
              </button>
            </div>

            <Button
              size="lg"
              onClick={onOpenDemo}
              data-testid="book-demo-btn"
              className="mt-8 w-full ff-btn-primary bg-[#E8650A] hover:bg-[#D15A1F] h-12 text-base font-semibold"
            >
              Book a Demo
            </Button>
          </div>

          {/* Right: contact form card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            {submitted && (
              <div
                data-testid="contact-success-message"
                className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              >
                Thanks — your message is on its way to our team. {"We'll"} reply within 24 hours.
              </div>
            )}
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="space-y-1.5">
                <Label htmlFor="cf-name">Name</Label>
                <Input
                  id="cf-name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Cooper"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-email">Email</Label>
                <Input
                  id="cf-email"
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-subject">Subject</Label>
                <Input
                  id="cf-subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="How can we help?"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-message">Message</Label>
                <Textarea
                  id="cf-message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a little more…"
                  rows={5}
                  required
                />
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full ff-btn-primary bg-[#E8650A] hover:bg-[#D15A1F] h-12 text-base font-semibold"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [vendors, setVendors] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeCuisine, setActiveCuisine] = useState(null);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [demoOpen, setDemoOpen] = useState(false);

  const scrollToContact = (e) => {
    if (e) e.preventDefault();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/public/vendors/live");
        if (!cancelled) setVendors(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load featured vendors:", err);
      } finally {
        if (!cancelled) setLoadingVendors(false);
      }
    })();
    (async () => {
      try {
        const res = await api.get("/public/events/upcoming", { params: { limit: 6 } });
        if (!cancelled) setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load upcoming events:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openNowCount = useMemo(() => vendors.filter((v) => v.is_live).length, [vendors]);

  const miniMapMarkers = useMemo(
    () =>
      vendors
        .filter((v) => v.is_live && v.lat != null && v.lng != null)
        .map((v) => ({
          vendor_id: v.vendor_id,
          business_name: v.business_name,
          lat: Number(v.lat),
          lng: Number(v.lng),
          is_live: true,
          logo_url: v.logo_url ? fileUrl(v.logo_url) : null,
          cuisine_tags: v.cuisine_tags || [],
          cuisine_type: v.cuisine_type,
        })),
    [vendors]
  );

  const miniMapCenter = useMemo(() => {
    if (miniMapMarkers.length === 0) return [39.5, -98.35]; // US center fallback
    const lat =
      miniMapMarkers.reduce((s, m) => s + m.lat, 0) / miniMapMarkers.length;
    const lng =
      miniMapMarkers.reduce((s, m) => s + m.lng, 0) / miniMapMarkers.length;
    return [lat, lng];
  }, [miniMapMarkers]);

  const miniMapZoom = miniMapMarkers.length === 0 ? 4 : miniMapMarkers.length === 1 ? 12 : 10;

  const displayedEvents = useMemo(() => {
    const list = isSeedEventVisible() ? [SEED_EVENT, ...events] : events;
    return list;
  }, [events]);

  const filteredVendors = useMemo(() => {
    const list = vendors.slice();
    if (!activeCuisine) return list.slice(0, 6);
    const lower = activeCuisine.toLowerCase();
    return list
      .filter((v) => (v.cuisine_tags || []).some((t) => (t || "").toLowerCase().includes(lower)))
      .slice(0, 6);
  }, [vendors, activeCuisine]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="sm" withTagline />
          <nav className="flex items-center gap-1 sm:gap-2">
            <Link
              to="/map"
              className="hidden md:inline-flex text-sm font-medium text-slate-700 hover:text-[#E8650A] px-3 py-2 transition"
              data-testid="nav-public-map"
            >
              Live Map
            </Link>
            <Link
              to="/academy"
              className="hidden md:inline-flex text-sm font-medium text-slate-700 hover:text-[#E8650A] px-3 py-2 transition"
              data-testid="nav-academy"
            >
              Academy
            </Link>
            <Link
              to="/permit-finder"
              className="hidden md:inline-flex text-sm font-medium text-slate-700 hover:text-[#E8650A] px-3 py-2 transition"
              data-testid="nav-permit-finder"
            >
              Permit Finder
            </Link>
            <Link
              to="/marketing"
              className="hidden md:inline-flex text-sm font-medium text-slate-700 hover:text-[#E8650A] px-3 py-2 transition"
              data-testid="nav-marketing"
            >
              Marketing
            </Link>
            <Link to="/login" data-testid="nav-login">
              <Button variant="outline" size="sm">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button size="sm" className="ff-btn-primary">Start free</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-orange-50/60 via-white to-white" />
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#E8650A] bg-[#FDECE2] px-3 py-1 rounded-full">
                <Flame size={14} /> {openNowCount > 0 ? `${openNowCount} open now` : "Live food truck network"}
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
                Find food trucks<br />
                <span className="text-[#E8650A]">near you, right now.</span>
              </h1>
              <p className="mt-4 text-base sm:text-lg font-semibold text-[#E8650A] tracking-tight">
                Find. Order. Serve: <span className="text-slate-900">Street Food Simplified</span>
              </p>
              <p className="mt-3 text-slate-600 text-base max-w-xl">
                {"#FoodieFinder, a mobile and web platform built to power the next generation of food truck commerce. Discover hand-picked food trucks, festivals and pop-ups in your city — track who's open, what's cooking, and where they'll roll up next."}
              </p>

              {/* Search-style CTA */}
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/map" className="flex-1" data-testid="cta-explore-map">
                  <div className="h-12 w-full flex items-center justify-between gap-3 bg-white border border-slate-200 hover:border-[#E8650A]/50 rounded-full px-5 shadow-sm transition group">
                    <div className="flex items-center gap-3 text-slate-500">
                      <Search className="h-4 w-4" />
                      <span className="text-sm">Search trucks, cuisines, neighborhoods…</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#E8650A] group-hover:translate-x-0.5 transition">
                      Explore <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
                <Link to="/register" data-testid="cta-register-hero">
                  <Button size="lg" className="ff-btn-primary h-12 rounded-full px-6">
                    List your truck
                  </Button>
                </Link>
              </div>

              {/* Cuisine chips */}
              <div className="mt-7" data-testid="cuisine-chip-row">
                <div className="flex flex-wrap gap-2">
                  {CUISINES.map((c) => {
                    const active = activeCuisine === c;
                    return (
                      <button
                        key={c}
                        onClick={() => setActiveCuisine(active ? null : c)}
                        data-testid={`cuisine-chip-${c.toLowerCase()}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                          active
                            ? "bg-[#E8650A] text-white border-[#E8650A]"
                            : "bg-white text-slate-700 border-slate-200 hover:border-[#E8650A]/40 hover:text-[#E8650A]"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right preview card */}
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="landing-mini-map-card">
                <div className="aspect-[4/3] relative">
                  <div className="absolute inset-0" data-testid="landing-mini-map">
                    <MapView
                      center={miniMapCenter}
                      zoom={miniMapZoom}
                      markers={miniMapMarkers}
                      height="100%"
                      interactive={false}
                      testid="landing-mini-map-view"
                    />
                  </div>
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 shadow-sm pointer-events-none z-[400]">
                    <span className="ff-live-dot" /> {openNowCount} live now
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Live network</div>
                    <div className="text-lg font-bold">{vendors.length} trucks · {openNowCount} open</div>
                  </div>
                  <Link to="/map" data-testid="cta-view-map-card">
                    <Button variant="outline" size="sm">View map</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Food Trucks */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="featured-trucks-section">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Featured Food Trucks</h2>
            <p className="mt-2 text-slate-500">Hand-picked favorites trending this week</p>
          </div>
          <Link to="/map" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#E8650A] hover:underline" data-testid="see-all-trucks">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loadingVendors ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-3 bg-slate-100 rounded w-2/3 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-16 bg-orange-50/40 border border-dashed border-orange-200 rounded-2xl">
            <Truck className="h-10 w-10 text-[#E8650A] mx-auto" />
            <p className="mt-3 text-slate-600 font-medium">
              {activeCuisine ? `No ${activeCuisine} trucks live right now.` : "No trucks live just yet."}
            </p>
            <p className="text-sm text-slate-500 mt-1">Check back soon — new spots roll in every day.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVendors.map((v) => (
              <VendorCard key={v.vendor_id} vendor={v} />
            ))}
          </div>
        )}
      </section>

      {/* Upcoming Events */}
      <section className="bg-slate-50/60 border-y border-slate-200" data-testid="upcoming-events-section">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Upcoming Food Truck Events</h2>
              <p className="mt-2 text-slate-500">Festivals, pop-ups, and weekly meets</p>
            </div>
          </div>

          {displayedEvents.length === 0 ? (
            <div className="text-center py-12 bg-white border border-dashed border-slate-200 rounded-2xl">
              <Calendar className="h-10 w-10 text-[#E8650A] mx-auto" />
              <p className="mt-3 text-slate-600 font-medium">No upcoming events listed yet.</p>
              <p className="text-sm text-slate-500 mt-1">Vendors can publish events from their dashboard.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedEvents.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Dual CTA banner */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-3xl bg-[#E8650A] text-white p-8 sm:p-10" data-testid="cta-vendor-banner">
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-white/10" />
            <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-white/5" />
            <Truck className="h-10 w-10" />
            <h3 className="mt-4 text-2xl font-bold tracking-tight">Are you a food truck owner?</h3>
            <p className="mt-2 text-white/90 max-w-sm">
              Join FoodieFinder and get discovered by hungry customers nearby.
            </p>
            <div className="mt-6">
              <Link to="/register" data-testid="cta-list-your-truck">
                <Button size="lg" className="bg-white text-[#E8650A] hover:bg-white/90">
                  List Your Truck <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 sm:p-10" data-testid="cta-mobile-app-banner">
            <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-[#E8650A]/20" />
            <Smartphone className="h-10 w-10 text-[#E8650A]" />
            <h3 className="mt-4 text-2xl font-bold tracking-tight">Take it on the go</h3>
            <p className="mt-2 text-white/70 max-w-sm">
              Get the FoodieFinder mobile app for live tracking and exclusive rewards.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button size="lg" disabled className="bg-white/10 text-white hover:bg-white/15 border border-white/15">
                Get the App
              </Button>
              <span className="self-center text-xs font-medium text-white/60">Coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-slate-200 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">What our foodies say</h2>
            <p className="mt-2 text-slate-500">Real reviews from real eaters</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition"
              >
                <Quote className="absolute top-4 right-4 h-6 w-6 text-[#E8650A]/20" />
                <div className="flex gap-1 text-amber-400">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-amber-400" />
                  ))}
                </div>
                <p className="mt-4 text-slate-800 leading-relaxed">“{t.quote}”</p>
                <p className="mt-4 text-sm font-semibold text-slate-500">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions? Contact */}
      <ContactSection onOpenDemo={() => setDemoOpen(true)} />

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-12 gap-10">
            {/* Brand + tagline */}
            <div className="md:col-span-5">
              <BrandMark size="md" asLink={false} testid="footer-brand" withTagline />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-sm" data-testid="footer-description">
                #FoodieFinder, a mobile and web platform built to power the next generation of food truck commerce.
              </p>
              <a
                href="https://www.facebook.com/1foodiefinder"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#E8650A] transition"
              >
                <Facebook className="h-4 w-4" /> facebook.com/1foodiefinder
              </a>
            </div>

            {/* Links */}
            <div className="md:col-span-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                <li><Link to="/map" className="hover:text-[#E8650A]">Live Map</Link></li>
                <li><Link to="/academy" className="hover:text-[#E8650A]" data-testid="footer-academy">Academy</Link></li>
                <li><Link to="/permit-finder" className="hover:text-[#E8650A]" data-testid="footer-permit-finder">Permit Finder</Link></li>
                <li><Link to="/marketing" className="hover:text-[#E8650A]" data-testid="footer-marketing">Marketing</Link></li>
                <li><Link to="/register" className="hover:text-[#E8650A]">For vendors</Link></li>
                <li><Link to="/login" className="hover:text-[#E8650A]">Sign in</Link></li>
                <li>
                  <a
                    href="#contact"
                    onClick={scrollToContact}
                    data-testid="footer-contact-link"
                    className="hover:text-[#E8650A] cursor-pointer"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Business info */}
            <div className="md:col-span-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-900">Foodie Finder LLC</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600" data-testid="footer-business-info">
                <li className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-[#E8650A] flex-shrink-0" />
                  <span>EIN: 0015040426</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#E8650A] flex-shrink-0" />
                  <span>10 Moore Rd, Lebanon, PA 17046</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 text-[#E8650A] flex-shrink-0" />
                  <a
                    href="mailto:support@foodie-finder.com"
                    data-testid="footer-support-email"
                    className="hover:text-[#E8650A]"
                  >
                    support@foodie-finder.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-xs text-slate-400">© {new Date().getFullYear()} Foodie Finder LLC · All rights reserved.</div>
            <div className="text-xs text-slate-400">Find. Order. Serve: Street Food Simplified.</div>
          </div>
        </div>
      </footer>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
